<template>
  <div class="video-item" :class="{ 'processing-item': isProcessing }">
    <div class="video-thumbnail-container" :class="{ 'processing-thumbnail': isProcessing }" @click="!isProcessing && $emit('play', item, $event)">
      <!-- Processing State -->
      <div v-if="isProcessing" class="processing-indicator">
        <q-circular-progress show-value :value="item.progress || 0" size="60px" :thickness="0.12" color="primary" track-color="grey-3">
          <span class="progress-percentage">{{ Math.round(item.progress || 0) }}%</span>
        </q-circular-progress>
        <div class="processing-text">{{ item.timeRemaining || 'Generating video...' }}</div>
      </div>

      <!-- Video or Error State -->
      <template v-else>
        <video v-if="item.url && !item.hasError" class="video-thumbnail" :src="item.url" preload="metadata"></video>
        <div v-else class="video-placeholder-thumbnail">
          <q-icon :name="getErrorIcon(item)" size="24px" color="grey-5" />
          <div class="placeholder-text">{{ getErrorText(item) }}</div>
        </div>
        <div v-if="item.url && !item.hasError" class="duration">6s</div>
      </template>
    </div>

    <div class="video-info">
      <div class="video-details">
        <h5 class="video-title">AI Generated Video</h5>
        <div class="video-type">{{ item.cameraEffect || item.label || 'AI Generated' }}</div>
      </div>
      <div class="video-actions">
        <q-btn flat round size="sm" :icon="isProcessing ? 'hourglass_empty' : 'play_arrow'" :disable="isProcessing || item.hasError" @click="!isProcessing && $emit('play', item, $event)" />
        <q-btn flat round size="sm" icon="file_download" :disable="isProcessing || item.hasError || !item.url" @click="$emit('download', item.url)" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'VideoItem',
  props: {
    item: {
      type: Object,
      required: true
    },
    isProcessing: {
      type: Boolean,
      default: false
    }
  },
  emits: ['play', 'download'],
  methods: {
    getErrorIcon(item) {
      if (item.status === 'stuck') return 'schedule';
      if (item.status === 'failed') return 'error';
      return 'videocam_off';
    },
    getErrorText(item) {
      if (item.status === 'stuck') return 'Stuck';
      if (item.status === 'failed') return 'Failed';
      return 'No video';
    }
  }
};
</script>

<style lang="scss" scoped>
.video-item {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px var(--card-shadow-color);
  background: var(--secondary-bg-color);
  height: 230px;
  display: flex;
  flex-direction: column;
  transition: transform 0.2s ease;
  border: 1px solid var(--border-outlined-btn-color);

  &.active {
    box-shadow: 0 0 0 2px var(--main-btn-color);
  }

  &.processing-item {
    height: 230px;
  }

  .video-thumbnail-container {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9;
    overflow: hidden;
    background: var(--main-bg-color);

    &.processing-thumbnail {
      background: linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%);
      border: 2px dashed #ddd;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
    }
  }

  .video-thumbnail {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .video-error-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: white;

    .error-text {
      font-size: 12px;
      margin-top: 8px;
      text-align: center;
    }
  }

  .duration {
    position: absolute;
    bottom: 8px;
    right: 8px;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 4px;
  }

  .video-info {
    padding: 12px;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .video-details {
    flex: 1;
    overflow: hidden;
  }

  .video-title {
    @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 500);
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .video-type {
    @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color), $weight: 400);
    margin-top: 4px;
  }

  .video-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

.processing-item {
  opacity: 0.9;
}

.processing-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
  width: 90%;
  max-width: 200px;
}

.progress-container {
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-circle {
  @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color), $weight: 600);
}

.progress-percentage {
  @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color), $weight: 600);
}

.processing-text {
  @include inter-font($size: 14px, $bolded: true, $color: var(--main-text-color), $weight: 500);
}

.time-remaining {
  @include inter-font($size: 12px, $bolded: false, $color: var(--secondary-text-color), $weight: 400);
}

.video-placeholder-thumbnail {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: var(--main-bg-color);
  gap: 8px;

  .placeholder-text {
    font-size: 12px;
    color: var(--secondary-text-color);
  }
}
</style>
